import { type FC, useCallback, useMemo } from 'react'

import { useNavigate } from '@tanstack/react-router'
import { type Except } from 'type-fest'

import { PromotionForm, type PromotionFormProps } from '@/components/organisms/PromotionForm'
import { collections } from '@/schema'
import { usePromotion } from '@/state/promotions/usePromotion'

import { fromFormValues } from './fromFormValues'
import { toFormValues } from './toFormValues'

export interface RegionPromotionUpdateFormPageProps extends Except<
  PromotionFormProps,
  'values' | 'disabled' | 'onFormSubmit' | 'onDelete' | 'storagePath' | 'onDirtyChange'
> {
  initiativeId: string
  promotionId: string
  onDirtyChange?: (isDirty: boolean) => void
}

export const PromotionUpdateFormPage: FC<RegionPromotionUpdateFormPageProps> = ({
  initiativeId,
  promotionId,
  onDirtyChange,
  enabledPlacements,
  ...props
}) => {
  const { data: promotion } = usePromotion({ promotionId })
  const formValues = useMemo(() => toFormValues(promotion), [promotion])
  const navigate = useNavigate()

  const deletePromotion = useCallback(async () => {
    await collections.promotions.delete(promotionId)
    await navigate({ to: '..' })
  }, [promotionId, navigate])

  const savePromotion = useCallback<PromotionFormProps['onFormSubmit']>(
    async (formData) => {
      await collections.promotions.update(promotionId, fromFormValues(formData, enabledPlacements))
      await navigate({ to: '..' })
    },
    [enabledPlacements, promotionId, navigate]
  )

  return (
    <div className="h-full p-8">
      <PromotionForm
        promotionType={promotion?.type}
        storagePath={`promotions/${initiativeId}`}
        disabled={!promotion}
        onDelete={!promotion?.used ? deletePromotion : undefined}
        onFormSubmit={savePromotion}
        onDirtyChange={onDirtyChange}
        enabledPlacements={enabledPlacements}
        values={formValues}
        {...props}
      />
    </div>
  )
}
